import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useMemo } from 'react';

import { isReplayMode } from '../utils/replayToken';
import { isShareMode } from '../utils/shareToken';
import { UserRole } from '../utils/user';
import { FabricConnectionCreationRole } from './constants';

type EnvConfig = {
  fabrics: {
    unifiedFabricUX: boolean;
    orchestration: {
      connectionsCrud: {
        allowed: boolean;
        role: UserRole;
        disabledReason?: 'PROPHECY_AUTOMATE_DISABLED' | 'SHARE_OR_REPLAY_PROJECT';
      };
      allowConnectionMonitoring: boolean;
    };
    fabricAuthTokenDisabled: boolean;
    databricks: {
      connectionCreationDisabled: boolean;
    };
    crud: {
      creationVisible: boolean;
      role: UserRole;
      showProviderDropdownForOrchestration: boolean;
      disabledReason?: 'EDITION_RESTRICTION' | 'SAP_ENABLED' | 'ROLE_RESTRICTION';
      disabledMessage?: string;
    };
  };
  observability: {
    enabled: boolean;
  };
  sql: {
    pipelines: {
      allowSchedule: boolean;
      enableOrchestrationGems: boolean;
      allowPipelineDryRun?: boolean;
    };
    jobs: {
      allowCreate: boolean;
      allowSchedule: boolean;
    };
    allowFileUpload: boolean;
    allowChatFileUpload: boolean;
    simpleVersionControl: {
      enableProjectDeployment: boolean;
    };
  };
  orchestration: {
    enabled: boolean;
  };
};

const defaultEnvConfig: EnvConfig = {
  fabrics: {
    unifiedFabricUX: false,
    orchestration: {
      connectionsCrud: {
        allowed: true,
        role: UserRole.All
      },
      allowConnectionMonitoring: false
    },
    fabricAuthTokenDisabled: false,
    crud: {
      creationVisible: true,
      role: UserRole.All,
      showProviderDropdownForOrchestration: false
    },
    databricks: {
      connectionCreationDisabled: false
    }
  },
  observability: {
    enabled: true
  },
  sql: {
    pipelines: {
      allowSchedule: true,
      enableOrchestrationGems: true
    },
    jobs: {
      allowCreate: true,
      allowSchedule: true
    },
    allowFileUpload: true,
    allowChatFileUpload: true,
    simpleVersionControl: {
      enableProjectDeployment: true
    }
  },
  orchestration: {
    enabled: true
  }
};

export const useEnvConfig = () => {
  const {
    isProphecyAutomateDisabled,
    showOnlyAirflowConnections,
    isSapEnabled,
    isUnifiedFabricEnabled,
    isConnectionMonitoringEnabled,
    fabricConnectionCreationRole,
    getIsFabricAccessibleToClusterAdmin,
    isOrchestrationDisabled,
    isFabricAuthTokenDisabled
  } = useGlobalVariables();
  const isShareOrReplayProject = isShareMode() || isReplayMode();

  const envConfig = useMemo(() => {
    const config = structuredClone(defaultEnvConfig);

    if (getIsFabricAccessibleToClusterAdmin) {
      config.fabrics.crud = {
        ...config.fabrics.crud,
        role: UserRole.ClusterAdmin,
        disabledReason: 'ROLE_RESTRICTION',
        disabledMessage: 'Only Cluster Admins can create new fabrics'
      };
    }

    if (isProphecyAutomateDisabled) {
      config.fabrics.orchestration.connectionsCrud = {
        allowed: false,
        disabledReason: 'PROPHECY_AUTOMATE_DISABLED',
        role: UserRole.All
      };
      config.sql.pipelines.enableOrchestrationGems = false;
      config.sql.pipelines.allowSchedule = false;
      config.observability.enabled = false;
      config.sql.jobs.allowCreate = false;
      config.sql.jobs.allowSchedule = false;
      config.sql.allowFileUpload = false;
      config.sql.simpleVersionControl.enableProjectDeployment = false;
    }

    // allow job creation and new connection creation even when isProphecyAutomateDisabled is false
    if (showOnlyAirflowConnections) {
      config.fabrics.orchestration.connectionsCrud.allowed = true;
      config.sql.jobs.allowCreate = true;
    }

    // fabriConnectionCreationRole is set, this takes precedence over isProphecyAutomateDisabled / showOnlyAirflowConnections
    if (
      fabricConnectionCreationRole === FabricConnectionCreationRole.ClusterAdmin ||
      fabricConnectionCreationRole === FabricConnectionCreationRole.TeamAdmin
    ) {
      config.fabrics.orchestration.connectionsCrud = {
        allowed: true,
        role:
          fabricConnectionCreationRole === FabricConnectionCreationRole.ClusterAdmin
            ? UserRole.ClusterAdmin
            : UserRole.TeamAdmin
      };
    }

    if (isShareOrReplayProject) {
      config.fabrics.orchestration.connectionsCrud = {
        allowed: false,
        disabledReason: 'SHARE_OR_REPLAY_PROJECT',
        role: UserRole.All
      };
    }

    if (isUnifiedFabricEnabled) {
      config.fabrics.unifiedFabricUX = true;
    }

    if (isSapEnabled) {
      config.fabrics.crud = {
        showProviderDropdownForOrchestration: false,
        creationVisible: false,
        disabledReason: 'SAP_ENABLED',
        role: UserRole.All
      };
      config.sql.pipelines.allowPipelineDryRun = true;
    }

    if (isConnectionMonitoringEnabled) {
      config.fabrics.orchestration.allowConnectionMonitoring = true;
    }

    if (isOrchestrationDisabled) {
      config.orchestration.enabled = false;
    }

    if (isFabricAuthTokenDisabled) {
      config.fabrics.fabricAuthTokenDisabled = true;
      config.fabrics.databricks.connectionCreationDisabled = true;
    }

    return config;
  }, [
    isProphecyAutomateDisabled,
    showOnlyAirflowConnections,
    isShareOrReplayProject,
    isUnifiedFabricEnabled,
    isSapEnabled,
    isConnectionMonitoringEnabled,
    fabricConnectionCreationRole,
    getIsFabricAccessibleToClusterAdmin,
    isOrchestrationDisabled,
    isFabricAuthTokenDisabled
  ]);

  return envConfig;
};
